import { NonConformityStatus } from '../../../utils/qualityControlConstants';

export function formatDateBR(isoDate: string | null | undefined): string {
  if (!isoDate) return '-';
  const [year, month, day] = isoDate.slice(0, 10).split('-');
  if (!year || !month || !day) return '-';
  return `${day}/${month}/${year}`;
}

export function formatDateTimeBR(
  isoDateTime: string | null | undefined,
): string {
  if (!isoDateTime) return '-';
  const date = new Date(isoDateTime);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * "Hoje" no fuso do navegador, em yyyy-mm-dd. Não usar toISOString(): ele
 * converte para UTC e, no Brasil (UTC-3), a partir das 21h já devolve o dia
 * seguinte — o selo "Vencida" divergia dos indicadores do backend.
 */
export function toLocalIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function isNonConformityOverdue(
  status: NonConformityStatus,
  dueDate: string,
  referenceDate: Date = new Date(),
): boolean {
  if (status === 'closed') return false;
  return dueDate < toLocalIsoDate(referenceDate);
}

export type IndicatorKey = 'open' | 'inProgress' | 'overdue' | 'closed';

/** Filtro de status da lista: o que os cards de indicador ligam/desligam. */
export interface ListStatusFilter {
  status: string; // '' | NonConformityStatus
  onlyOverdue: boolean;
}

const LIST_STATUS_VALUES: NonConformityStatus[] = [
  'open',
  'in_progress',
  'closed',
];

export function filterFromIndicator(key: IndicatorKey): ListStatusFilter {
  if (key === 'overdue') return { status: '', onlyOverdue: true };
  return {
    status: key === 'inProgress' ? 'in_progress' : key,
    onlyOverdue: false,
  };
}

/** Card destacado para o filtro atual (null se nenhum card corresponde). */
export function indicatorFromFilter({
  status,
  onlyOverdue,
}: ListStatusFilter): IndicatorKey | null {
  if (onlyOverdue) return status ? null : 'overdue';
  if (status === 'in_progress') return 'inProgress';
  if (status === 'open' || status === 'closed') return status;
  return null;
}

/**
 * Lê `?status=` e `?overdue=1` da URL da lista (vindos de um card do
 * Dashboard ou de um F5). Valor desconhecido é ignorado.
 */
export function parseListStatusFilter(search: string): ListStatusFilter {
  const params = new URLSearchParams(search);
  const status = params.get('status') || '';

  return {
    status: LIST_STATUS_VALUES.includes(status as NonConformityStatus)
      ? status
      : '',
    onlyOverdue: params.get('overdue') === '1',
  };
}

/** Querystring da lista para o filtro (mantém `demo=1` quando ativo). */
export function buildListStatusSearch(
  { status, onlyOverdue }: ListStatusFilter,
  demo = false,
): string {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (onlyOverdue) params.set('overdue', '1');
  if (demo) params.set('demo', '1');
  const search = params.toString();
  return search ? `?${search}` : '';
}

export interface IndicatorCounts {
  open: number;
  inProgress: number;
  closed: number;
  overdue: number;
  total: number;
}

/**
 * Indicadores da lista que está na tela (já filtrada), com a mesma regra de
 * "vencida" do selo da tabela — assim os cards sempre batem com as linhas.
 */
export function countNonConformityIndicators(
  items: { status: NonConformityStatus; due_date: string }[],
  referenceDate: Date = new Date(),
): IndicatorCounts {
  return items.reduce<IndicatorCounts>(
    (acc, item) => ({
      open: acc.open + (item.status === 'open' ? 1 : 0),
      inProgress: acc.inProgress + (item.status === 'in_progress' ? 1 : 0),
      closed: acc.closed + (item.status === 'closed' ? 1 : 0),
      overdue:
        acc.overdue +
        (isNonConformityOverdue(item.status, item.due_date, referenceDate)
          ? 1
          : 0),
      total: acc.total + 1,
    }),
    { open: 0, inProgress: 0, closed: 0, overdue: 0, total: 0 },
  );
}
